import React, { useEffect } from 'react'
import {
  TripActivity as RecentActivityHotel,
  RecentActivityRentalCar
} from '@pcln/marketing'
import { AbandonedFlight } from '@pcln/trip-activity-components'
import useBootstrapData from '@/hooks/useBootstrapData'
import useSeti from '@/hooks/useSeti'
import { handleToggleFavorite } from './utils'
import {
  fireAbandonedSelectionClickEvent,
  fireAbandonedSelectionLoadEvent,
  fireAddToWishlistToggleEvent
} from './ga4'
import { type AbandonedSelection } from './types'

export default function AbandonedSelection({
  abandonedSelection,
  analyticsCategory,
  useSmallerView = false,
  isTripGroupedSelection = false
}: {
  abandonedSelection: AbandonedSelection
  analyticsCategory: 'PCLN_HOME' | 'HOTEL_HOME'
  useSmallerView?: boolean
  isTripGroupedSelection?: boolean
}) {
  const {
    appName,
    appVersion,
    signInStatus: { signedIn },
    webstats: { clientGUID: cguid }
  } = useBootstrapData()
  const hotelFavSeti =
    useSeti('HP_PUWYLO_FAVORITE', signedIn) === 'VARIANT' && signedIn

  useEffect(() => {
    if (abandonedSelection) {
      fireAbandonedSelectionLoadEvent(
        abandonedSelection,
        analyticsCategory,
        isTripGroupedSelection
      )
    }
  }, [abandonedSelection, analyticsCategory, isTripGroupedSelection])

  if (abandonedSelection.productType === 'cars') {
    return (
      <RecentActivityRentalCar
        {...abandonedSelection}
        subheadline={
          <>
            Pick up Location: {abandonedSelection.pickupLocation}
            {abandonedSelection.isDropOffAtDifferentLocation && (
              <>
                <br />
                Drop Off Location: {abandonedSelection.returnLocation}
              </>
            )}
          </>
        }
        onClick={() => {
          fireAbandonedSelectionClickEvent(
            abandonedSelection,
            analyticsCategory,
            isTripGroupedSelection
          )
        }}
        isSmallerView={useSmallerView}
        altText="Trip activity image"
        ariaLabel="trip activity button"
      />
    )
  }
  if (abandonedSelection.productType === 'flights') {
    const { headline, linkUrl } = abandonedSelection
    return (
      <AbandonedFlight
        {...abandonedSelection}
        heading={headline}
        url={linkUrl}
        buttonText="Continue Search"
        onClick={() => {
          fireAbandonedSelectionClickEvent(
            abandonedSelection,
            analyticsCategory,
            isTripGroupedSelection
          )
          window.open(linkUrl)
        }}
        isSmallerView={useSmallerView}
      />
    )
  }

  return hotelFavSeti ? (
    <RecentActivityHotel
      {...abandonedSelection}
      onClick={() => {
        fireAbandonedSelectionClickEvent(
          abandonedSelection,
          analyticsCategory,
          isTripGroupedSelection
        )
      }}
      isSmallerView={useSmallerView}
      altText="Trip activity image"
      ariaLabel="trip activity button"
      hotelPriceDetails={
        !isTripGroupedSelection &&
        abandonedSelection.hotelTotalPriceWithTaxesAndFeesAtAbandonment?.amount
          ? {
              price:
                abandonedSelection.hotelTotalPriceWithTaxesAndFeesAtAbandonment
                  .amount
            }
          : undefined
      }
      onFavoriteToggle={async (isNowFavorite: boolean) => {
        fireAddToWishlistToggleEvent(isNowFavorite, analyticsCategory)
        if (abandonedSelection.id && abandonedSelection.cityId) {
          await handleToggleFavorite(
            {
              hotelId: abandonedSelection.id,
              cityId: abandonedSelection.cityId,
              cguid,
              appName,
              appVersion
            },
            isNowFavorite
          )
        }
      }}
      initialFavoriteState={abandonedSelection.isFavorite}
    />
  ) : (
    <RecentActivityHotel
      {...abandonedSelection}
      onClick={() => {
        fireAbandonedSelectionClickEvent(
          abandonedSelection,
          analyticsCategory,
          isTripGroupedSelection
        )
      }}
      isSmallerView={useSmallerView}
      altText="Trip activity image"
      ariaLabel="trip activity button"
      hotelPriceDetails={
        !isTripGroupedSelection
          ? {
              price:
                abandonedSelection.hotelTotalPriceWithTaxesAndFeesAtAbandonment
                  .amount
            }
          : undefined
      }
    />
  )
}
